<template>
  <li
    class="event-card-wrapper"
    :class="{ 'is-expanded': isExpanded }"
  >
    <!-- Main Event Card Header -->
    <div class="event-card" @click="$emit('toggle-expand', event.id)">
      <div class="date-badge" :class="dateTagClass">
        <span class="day">{{ dayNumber }}</span>
        <span class="month">{{ monthName }}</span>
        <span v-if="event.isAllDay" class="badge-all-day">All Day</span>
        <span v-else class="time">{{ timeString }}</span>
      </div>

      <div class="event-info">
        <div class="title-row">
          <h4>{{ event.title }}</h4>
          <span v-if="relativeDateLabel" class="relative-tag">
            {{ relativeDateLabel }}
          </span>
        </div>
        <p v-if="event.description" class="description-preview">
          {{ event.description }}
        </p>
      </div>

      <div class="event-actions">
        <button class="btn-action edit" title="Edit Event" @click.stop="$emit('edit', event)">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
        </button>
        <button class="btn-action delete" title="Delete Event" @click.stop="$emit('delete', event.id)">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
        <span class="chevron-icon" title="Toggle Details">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </span>
      </div>
    </div>

    <!-- Expanded Drawer -->
    <Transition name="expand">
      <div v-if="isExpanded" class="event-details-drawer">
        <div class="drawer-content">
          <div class="detail-row highlight-row">
            <span class="detail-label">Starts In:</span>
            <span class="detail-value countdown-pill">
              ⏱️ {{ timeRemaining }}
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Schedule:</span>
            <span class="detail-value">
              {{ fullDate }}
              <template v-if="!event.isAllDay">at {{ timeString }}</template>
            </span>
          </div>

          <div v-if="event.description" class="detail-row">
            <span class="detail-label">Notes:</span>
            <p class="detail-description">{{ event.description }}</p>
          </div>

          <div class="drawer-actions">
            <button class="btn-drawer-edit" @click="$emit('edit', event)">
              Edit Event
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </li>
</template>

<script setup>
import { computed } from 'vue';
import {
  getDayNumber,
  getMonthName,
  getTimeString,
  formatFullDate,
  getTimeRemaining,
  getRelativeDateLabel,
  getDateTagClass,
} from '@/utils/date';

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  isExpanded: {
    type: Boolean,
    default: false
  },
  currentTime: {
    type: Date,
    default: () => new Date()
  },
});

defineEmits(['toggle-expand', 'edit', 'delete']);

const dayNumber = computed(() => getDayNumber(props.event.startTime));
const monthName = computed(() => getMonthName(props.event.startTime));
const timeString = computed(() => getTimeString(props.event.startTime));
const fullDate = computed(() => formatFullDate(props.event.startTime));
const timeRemaining = computed(() => getTimeRemaining(props.event.startTime, props.event.isAllDay, props.currentTime));
const relativeDateLabel = computed(() => getRelativeDateLabel(props.event.startTime, props.currentTime));
const dateTagClass = computed(() => getDateTagClass(props.event.startTime, props.currentTime));
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

.event-card-wrapper {
  border: 1px solid color.mix($light-brown, #ffffff, 50%);
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: $light-brown;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }

  &.is-expanded {
    border-color: color.mix($dark-blue, #ffffff, 40%);

    .chevron-icon {
      transform: rotate(180deg);
      color: $dark-blue;
    }
  }

  .event-card {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 1rem 1.25rem;
    cursor: pointer;
    user-select: none;

    .date-badge {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-width: 70px;
      padding: 0.5rem 0.75rem;
      background: color.mix($white, #ffffff, 60%);
      border: 1px solid color.mix($light-brown, #ffffff, 60%);
      border-radius: 10px;
      text-align: center;

      .day {
        font-size: 1.25rem;
        font-weight: 800;
        color: $dark-blue;
        line-height: 1;
      }

      .month {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        color: $grey-back;
        margin-top: 0.15rem;
      }

      .time {
        font-size: 0.7rem;
        font-weight: 600;
        color: $grey-93;
        margin-top: 0.15rem;
        white-space: nowrap;
      }

      .badge-all-day {
        margin-top: 0.2rem;
        font-size: 0.6rem;
        font-weight: 700;
        text-transform: uppercase;
        background: color.mix($light-yellow, #ffffff, 50%);
        color: $brown;
        padding: 0.1rem 0.35rem;
        border-radius: 4px;
      }

      &.is-today {
        background: color.mix($orange, #ffffff, 12%);
        border-color: color.mix($orange, #ffffff, 35%);

        .day, .month {
          color: $orange;
        }
      }

      &.is-tomorrow {
        background: color.mix($dark-blue, #ffffff, 10%);
        border-color: color.mix($dark-blue, #ffffff, 25%);

        .day, .month {
          color: $dark-blue;
        }
      }
    }

    .event-info {
      flex: 1;

      .title-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        h4 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: $secondary-color;
        }

        .relative-tag {
          font-size: 0.65rem;
          font-weight: 800;
          color: $orange;
          background: color.mix($orange, #ffffff, 15%);
          padding: 0.1rem 0.4rem;
          border-radius: 4px;
        }
      }

      .description-preview {
        margin: 0.2rem 0 0 0;
        font-size: 0.85rem;
        color: $grey-back;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }

    .event-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .btn-action {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: color.mix($white, #ffffff, 50%);
        border: 1px solid color.mix($light-brown, #ffffff, 60%);
        border-radius: 8px;
        cursor: pointer;
        color: $grey-back;

        &.edit:hover {
          background: color.mix($mid-blue, #ffffff, 10%);
          color: $mid-blue;
          border-color: color.mix($mid-blue, #ffffff, 30%);
        }

        &.delete:hover {
          background: color.mix($hot-pink, #ffffff, 15%);
          color: $hot-pink;
          border-color: color.mix($hot-pink, #ffffff, 40%);
        }
      }

      .chevron-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        color: $grey-93;
        transition: transform 0.2s ease, color 0.2s ease;
        margin-left: 0.25rem;
      }
    }
  }

  .event-details-drawer {
    border-top: 1px dashed color.mix($light-brown, #ffffff, 80%);
    background: color.mix($white, #ffffff, 30%);
    padding: 1rem 1.25rem;

    .drawer-content {
      display: flex;
      flex-direction: column;
      gap: 0.6rem;

      .detail-row {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        font-size: 0.85rem;

        .detail-label {
          font-weight: 700;
          color: $dark-pink;
          min-width: 75px;
        }

        .detail-value {
          color: $secondary-color;
          font-weight: 500;

          &.countdown-pill {
            font-weight: 700;
            color: $orange;
            background: color.mix($orange, #ffffff, 12%);
            padding: 0.15rem 0.5rem;
            border-radius: 6px;
          }
        }

        .detail-description {
          margin: 0;
          color: $secondary-color;
          line-height: 1.5;
          white-space: pre-line;
        }
      }

      .drawer-actions {
        margin-top: 0.4rem;
        display: flex;
        justify-content: flex-end;

        .btn-drawer-edit {
          background: transparent;
          border: 1px solid color.mix($mid-blue, #ffffff, 40%);
          color: $mid-blue;
          font-size: 0.8rem;
          font-weight: 600;
          padding: 0.3rem 0.75rem;
          border-radius: 6px;
          cursor: pointer;

          &:hover {
            background: $mid-blue;
            color: $white;
          }
        }
      }
    }
  }
}
</style>